import "@utils/axios.instance"

import "bootstrap-icons/font/bootstrap-icons.css"
import "@/app.css"

import * as Sentry from "@sentry/vue"
import { createApp } from "vue"
import { registerSW } from "virtual:pwa-register"
import App from "./App.vue"
const app = createApp(App)

import { createPinia } from "pinia"
const pinia = createPinia()
app.use(pinia)

import { router } from "@router/index"
app.use(router)

Sentry.init({
  app,
  dsn: import.meta.env.VITE_SENTRY_DSN,
  enabled: import.meta.env.PROD,
  release: import.meta.env.VITE_APP_VERSION,
  sendDefaultPii: true,
  integrations: [
    Sentry.browserTracingIntegration({ router }),
    Sentry.replayIntegration()
  ],
  tracesSampleRate: 0.1,
  tracePropagationTargets: [/^https:\/\/.*\.planific\.ro/, /^https:\/\/confirmare\.la/],
  replaysSessionSampleRate: 0.05,
  replaysOnErrorSampleRate: 1.0,
  environment: import.meta.env.MODE,
  beforeSend(event) {
    const message = event.exception?.values?.[0]?.value ?? ""
    if (message.includes("Method not found")) return null
    if (message.includes("Importing a module script failed")) return null
    if (message.includes("Failed to fetch dynamically imported module")) return null
    return event
  }
})

if (import.meta.env.PROD && "serviceWorker" in navigator) {
  registerSW({
    immediate: true,
    onRegisteredSW(_swUrl, registration) {
      registration?.update()
    },
    onRegisterError(error: unknown) {
      console.warn("Service worker registration failed:", error)
    }
  })
}

import { useAuthStore } from "@stores/models/auth.store"
router.isReady().then(() => {
  const auth = useAuthStore(pinia)
  const routeName = router.currentRoute.value.name

  if (routeName === "login-token" || routeName === "login-token-short") {
    app.mount("#app")
    return
  }

  auth
    .silentRefresh()
    .then(async isLoggedIn => {
      // A client whose session is still valid must land in their space, not on
      // the login form - which, being logged in, also carried a "Deconectare"
      // button on it (PL-70)
      if (isLoggedIn && router.currentRoute.value.name === "login") {
        await router.replace({ name: "events" })
      }
    })
    .catch(() => {
      // Landing on the login form is the safe fallback, nothing to report
    })
    .finally(() => {
      app.mount("#app")
    })
})
